import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    Checkbox, DialogContentText,
    FormControlLabel,
    FormHelperText,
    Grid,
    TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import cn from 'classnames';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { useLevels } from '@dyl/systems/LevelsManager';
import TaskContext from './TaskContext';
import useTaskField from './useTaskField';
import {
    CheckCircle as CircleChecked,
    RadioButtonUnchecked as CircleUnchecked,
} from '@mui/icons-material';
import useDialog from "@dyl/hooks/useDialog";

/*const CALIBRATION_STATUSES = [
    { label: 'Uncalibrated', name: 'uncalibrated'},
    { label: 'Part Calibrated', name: 'part_calibrated' },
    { label: 'Fully Calibrated', name: 'fully_calibrated'}
]*/

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
    scrollableField: {
        display: "flex",
        maxHeight: "120px",
        overflow: "hidden",
        position: "relative",
    },
    singleLineCenter: {
        display: "flex",
        alignItems: 'center',
        maxHeight: "80px",
        overflow: "hidden",
        position: "relative",
        paddingTop:"70px"
    },
    scrollableValue: {
        display: "block",
        maxHeight: "120px",
        overflowY: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        lineHeight: "1.5",
        width: "100%",
        padding: "0",
        textAlign: "left",
        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#aaa",
            borderRadius: "6px",
        },
    }
}));

/** @param {import('@mui/material').TextFieldProps} props */
function Item({ mdSize = 4, ...props }) {
    return (
        <Grid item xs={12} sm={6} md={mdSize}>
            <TextField
                variant='filled'
                fullWidth
                {...props}
            />
        </Grid>
    );
}

function TaskNameField() {
    const [taskName, setTaskName, error] = useTaskField('task_id_name');
    const [value, setValue] = useState(taskName);
    const onTaskNameChange = (e) => {
        const val = e.target.value;
        setValue(val);
        setTaskName(val);
    };
    const persist = () => {
        setTaskName(value);
    }
    // Since useTaskField hook is async, the value state is initialized as null
    // An effect is required to update the value properly.
    useEffect(() => {
        setValue(taskName);
    }, [taskName]);

    const onKeyUp = useCallback((event) => {
        if (event.code === 'Enter') { persist(); }
    });

    return (
        <Grid item xs={12} sm={6} md={7}>
            <TextField
                variant='filled'
                fullWidth
                id='task-id'
                label='Task ID'
                value={value ?? ''}
                onChange={onTaskNameChange}
                required
                error={!!error}
                helperText={error ?? ''}
                onKeyUp={onKeyUp}
                onBlur={persist}
            />
        </Grid>
    );
}

function ComponentIDField({ data, classes }) {
    const value = data?.test_names?.length? Array.isArray(data.test_names)? data.test_names.join(', '): data.test_names: "";

    return (
            <Item
                id='task-componentId'
                label='Component ID'
                disabled
                mdSize={9}
                InputProps={{
                    className: `${value.length < 150 ? classes.singleLineCenter : classes.scrollableField}`,
                }}
                multiline
                rows={3}
                value={value}
                inputProps={{
                    className: classes.scrollableValue
                }}
            />
    );
}

export default function TaskEditorHeader({ className: propsClass, save, ...other }) {
    const { isNew, skill, taskType, data } = useContext(TaskContext);
    const { globalLevel } = useLevels();
    const [anchorUpdated, setAnchorUpdated] = useState(0);
    const [ImperativeDialog, openDialog] = useDialog();

    const classes = useStyles(other);

    const [isAnchor, setIsAnchor] = useTaskField('anchor');
/*    const [calibrationStatus, setCalibrationStatus, calibrationStatusError] = useTaskField('calibration_status');
    const onCalibrationStatusChange = useChangeEvent(setCalibrationStatus);
    let initialCalibrationStatus = calibrationStatus;
    if (!calibrationStatus || calibrationStatus === 'not_set') {
        initialCalibrationStatus = 'uncalibrated';
    }*/

    const onAnchorClick = useCallback(async() => {
        if (isAnchor) {
            const confirmed = await new Promise(resolve => openDialog(
                'Are you sure?',
                <>
                    <DialogContentText>
                        Are you sure you want to unmark this task as an anchor flag?
                    </DialogContentText>
                </>,
                [
                    { text: 'Yes', onClick: () => resolve(true) },
                    { text: 'No', onClick: () => resolve(false) },
                ],
            ));
            if (confirmed) {
                setIsAnchor(!isAnchor);
                setAnchorUpdated(anchorUpdated + 1);
            }
        } else {
            setIsAnchor(!isAnchor);
            setAnchorUpdated(anchorUpdated + 1);
        }
    }, [isAnchor]);

    useEffect(() => {
        if (anchorUpdated) save();
    }, [anchorUpdated]);

    const isSpeaking = skill?.label?.toLowerCase() === 'speaking';

    return (
        <>
            <Grid
                container
                spacing={1}
                className={cn(classes.root, propsClass)}
                {...other}
            >
                {/* ANCHOR */}
                {!isSpeaking && !isNew &&
                    <Grid item xs={12} sm={12} md={12}>
                        <FormControlLabel
                            margin="normal"
                            label={'Anchor'}
                            control={
                                <Checkbox
                                    checked={!!isAnchor}
                                    onClick={onAnchorClick}
                                    icon={<CircleUnchecked/>}
                                    checkedIcon={<CircleChecked/>}
                                    color='primary'
                                />
                            }
                        />
                        <FormHelperText>Check if the task is going to be used as an anchor task to calibrate new versions.</FormHelperText>
                    </Grid>
                }

                {/* LEVEL */}
                <Item
                    id='task-level'
                    label='Language level'
                    value={globalLevel?.label ?? ''}
                    disabled
                    mdSize={2}
                />

                {/* SKILL */}
                <Item
                    id='task-skill'
                    label='Skill'
                    value={skill?.label ?? ''}
                    disabled // Derived from task type
                    mdSize={3}
                />

                {/* TASK TYPE */}
                <Item
                    id='task-type'
                    label='Task Type'
                    value={taskType?.label ?? ''}
                    disabled // Set prior to this screen loading
                    mdSize={4}
                />

                <Grid item xs={0} sm={2} md={2} />

                {/* COUNTRY */}
                {/* TODO: Check if this is still wanted */}
                <Item
                    id='task-country'
                    label='UK or US'
                    value='UK'
                    disabled
                    mdSize={2}
                />

                {/* CALIBRATION STATUS */}
{/*                {!isSpeaking &&
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            variant='filled'
                            fullWidth
                            disabled={isAnchor}
                            id='task-calibration-status'
                            label='Calibration Status'
                            value={initialCalibrationStatus}
                            defaultValue={initialCalibrationStatus}
                            onChange={onCalibrationStatusChange}
                            error={!!calibrationStatusError}
                        >
                            {CALIBRATION_STATUSES.map(l =>
                                <MenuItem key={l.name} value={l.name}> {l.label} </MenuItem>
                            )}
                        </TextField>
                    </Grid>
                }*/}

                {/* TASK ID */}
                <TaskNameField />

                {/* Component ID */}
                <ComponentIDField data={data} classes={classes} />
            </Grid>

            <ImperativeDialog style={{ zIndex: 9003 }}/>
        </>
    );
}